import React, { useState, useEffect } from 'react';
import { useLatestVersion } from '../data/changelogData';
import './Legal.css';

function Terms() {
  const version = useLatestVersion();
  const [activeTab, setActiveTab] = useState('acceptable-use');
  
  const sections = [
    { id: 'acceptable-use', title: 'Acceptable Use', icon: 'fa-scale-balanced' },
    { id: 'identity', title: 'Identity Verification', icon: 'fa-user-check' },
    { id: 'intelligence', title: 'Intelligence Disclaimer', icon: 'fa-microchip' },
    { id: 'sovereignty', title: 'Platform Sovereignty', icon: 'fa-shield-halved' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollPos = window.scrollY + 130;
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element && element.offsetTop <= scrollPos && (element.offsetTop + element.offsetHeight) > scrollPos) {
          setActiveTab(section.id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  return (
    <div className="legal-gov-container animate-up">
      <aside className="legal-gov-sidebar">
        <ul className="legal-gov-toc">
          {sections.map(sec => (
            <li key={sec.id} className="legal-gov-toc-item">
              <a 
                href={`#${sec.id}`} 
                className={`legal-gov-toc-link ${activeTab === sec.id ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById(sec.id).scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <i className={`fa-solid ${sec.icon}`} style={{width: '20px', marginRight: '10px', fontSize: '0.8rem'}}></i>
                {sec.title}
              </a>
            </li>
          ))}
        </ul>
      </aside>

      <main className="legal-gov-content">
        <div className="legal-gov-seal">
          <i className="fa-solid fa-gavel"></i>
        </div>

        <article className="legal-gov-document">
          <header className="legal-gov-header">
            <h1 className="legal-gov-title">Operational Terms</h1>
            <div className="legal-gov-meta">GOVERNANCE_STANDARD: {version} | EXECUTED_MARCH_2026</div>
          </header>

          <section id="acceptable-use" className="legal-gov-section">
            <h2>1. Acceptable Use</h2>
            <p>
              By accessing the DevTracker ecosystem, you agree to interface with all technical nodes 
              through standard, non-disruptive neural channels. The platform is designed for professional 
              representation and technical trajectory tracking.
            </p>
            <p>
              Operationally, you are prohibited from:
            </p>
            <ul>
              <li><strong>Scraping Assets:</strong> Automating the harvesting of neural dossiers or platform metadata.</li>
              <li><strong>Identifier Deception:</strong> Attempts to link identifiers not belonging to the dossier holder.</li>
              <li><strong>System Probing:</strong> Unauthorized testing of the Origin Core or DAIV-2000 logic engines.</li>
            </ul>
          </section>

          <section id="identity" className="legal-gov-section">
            <h2>2. Identity Verification</h2>
            <p>
              Users retain absolute sovereignty over the handles associated with their SUID. DevTracker's 
              <strong>Neural Integrity</strong> engine monitors for impersonation anomalies in real-time.
            </p>
            <p>
              The attachment of a platform identifier (GitHub, etc.) constitutes a formal declaration 
              of ownership. Confirmed <strong>Impersonation Discrepancies</strong> result in immediate 
              account nullification and revocation of all associated professional credentials.
            </p>
          </section>

          <section id="intelligence" className="legal-gov-section">
            <h2>3. Intelligence Disclaimer</h2>
            <p>
              Platform insights, including DNA Scores and Persona Badges, are provided as professional 
              navigational guidance. These metrics are extrapolated from public data via the DAIV-X 
              logic framework.
            </p>
            <p>
              While providing a high-fidelity representation of your technical standing, DevTracker 
              does not legally guarantee these scores as factual proof of vocational capacity for 
              formal background verification or employment screenings.
            </p>
          </section>

          <section id="sovereignty" className="legal-gov-section">
            <h2>4. Platform Sovereignty</h2>
            <p>
              DevTracker reserves the right to modify, suspend, or terminate account access if 
              professional standards are breached or if fraudulent identity mapping is detected. 
              The <strong>Neural Authority</strong> framework is the final arbiter of platform 
              integrity and dossier transparency.
            </p>
            <p>
              Use of the platform constitutes acceptance of these operational boundaries.
            </p>
          </section>

          <div style={{marginTop: '60px', paddingTop: '30px', borderTop: '1px solid var(--border-color)', opacity: 0.5, fontSize: '0.75rem', fontFamily: 'monospace'}}>
            AUTHORITY_HASH: 0X-DT-TERMS-PROTOCOL-V4_0_0_9-AUTH-ACTIVE
          </div>
        </article>
      </main>
    </div>
  );
}

export default Terms;
